import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'story');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} · Galgame资源大全 | galgame资源下载`}
        description={`浏览${activeCategory.name}类galgame资源，共收录${activeCategory.count}款作品。${activeCategory.description}`}
        keywords={`galgame${activeCategory.name}资源,galgame资源下载,${activeCategory.name}galgame推荐,galgame资源网站`}
        canonicalUrl={`https://galgame-resources.com/category/${activeSlug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${activeCategory.name} · Galgame资源大全`,
          description: activeCategory.description,
          url: `https://galgame-resources.com/category/${activeSlug}`,
        }}
      />

      <Navbar />
      <main>
        <div className="pt-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/story' },
                { label: activeCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {activeCategory.description} · 共收录 <strong className="text-primary-600">{activeCategory.count}</strong> 款galgame资源
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-3">
                          <span>{article.date}</span>
                          <span>{article.readTime}</span>
                        </div>
                        <span>{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}